/**
 * What a failed row means, and what to do about it.
 *
 * The runner records the raw error because that is what a bug report needs. A reviewer
 * needs the other thing: `row exceeded 600000ms` and `claude: usage limit reached` are
 * both true and neither says whether to wait, retry, or fix the CSV.
 *
 * Derived in the console rather than stored, so the wording can improve without a
 * migration and every row already in the database gets it.
 */

import { looksRateLimited } from '@platform/schema/generation';

export interface Explained {
  /** One line, in the reviewer's terms. */
  readonly title: string;
  /** What to do. Absent when there is nothing to do but wait. */
  readonly action?: string;
  /** Whether this is the run's fault or the content's — drives the console's severity. */
  readonly kind: 'wait' | 'retry' | 'fix' | 'unknown';
}

/**
 * Matched on the message rather than a code, because these arrive as prose: the job row
 * stores the error's text, and rows written before GEN-127 hold the Agent SDK's.
 *
 * Ordered most specific first. Anything unmatched falls through with the raw text, which
 * is better than a confident wrong guess.
 */
export function explainFailure(raw: string | undefined): Explained | undefined {
  if (raw === undefined || raw.trim() === '') return undefined;

  const text = raw.toLowerCase();

  // First: a picture's reason can carry the image vendor's 429, which is not Claude's.
  const pictures =
    /^(\d+) picture\(s\) not drawn, each tried twice: (.*)$/s.exec(raw);
  if (pictures !== null) {
    return {
      title: `${pictures[1] ?? ''} picture(s) could not be drawn. The lesson was written and kept.`,
      action: `${(pictures[2] ?? '').replace(/ — \d+ lesson\(s\) were written and kept\.$/, '')}. Run the row again to build the lesson with its pictures.`,
      kind: 'retry',
    };
  }

  if (looksRateLimited(raw)) {
    return {
      title: 'The Claude API is out of capacity.',
      action:
        'Nothing is lost. Wait for the rate limit to reset, or top up the API credit, and run the rows again.',
      kind: 'wait',
    };
  }

  if (text.includes('exceeded') && text.includes('ms')) {
    return {
      title: 'This row ran past its ten-minute budget and was stopped.',
      action:
        'A module of six lessons is close to the limit. Split the row into two modules, or give the description fewer lessons to write.',
      kind: 'retry',
    };
  }

  if (text.includes('143') || text.includes('sigterm')) {
    return {
      title: 'The server restarted while this row was running.',
      action: 'It is queued again automatically. Nothing to do.',
      kind: 'wait',
    };
  }

  if (text.includes('rejected by the schema') || text.includes('retries')) {
    // A publish rule that survived the writer's tries is the row's outcome — GEN-213 —
    // so it is shown rather than left in the tooltip.
    const refused = raw.split('rejected by the schema —')[1]?.trim();
    return {
      title: 'The planner could not produce a lesson in the required shape.',
      action: `${refused ? `Still refused on the last try: ${refused.replace(/\.$/gm, '').replaceAll('\n', '; ')}. ` : ''}Usually a thin CSV row. Give the module a fuller description and run it again.`,
      kind: 'fix',
    };
  }

  if (text.includes('max_turns')) {
    return {
      title: 'The planner used its turns without returning a lesson.',
      action: 'Run the row again. A repeat is worth reporting.',
      kind: 'retry',
    };
  }

  if (text.includes('enoent') || text.includes('skill')) {
    return {
      title: 'The writing skills could not be loaded.',
      action:
        'A deployment problem rather than a content one — the server cannot see .claude/skills.',
      kind: 'fix',
    };
  }

  return { title: raw, kind: 'unknown' };
}
